import { PlusOutlined } from '@ant-design/icons';
import { Button, Card, Form, Input, Modal, Table } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { API } from '../../../api';
import { notifyError, showConfirm } from '../../../store/utils';
import { NetworkResult } from '../../../common/CommonResult';

const BarcodeSettingsList = () => {
  const [state, setState] = useState<NetworkResult<any[]>>({
    loading: true,
    result: [],
  });
  const [edit, setEdit] = useState<any>({ visible: false, data: null });

  const fetchData = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const response = await API.get({ apiVersion: 'api' })(`/settings/barcode`);
      setState({
        loading: false,
        result: response.result,
      });
    } catch (error: any) {
      notifyError(error.message);
      setState((prev) => ({ ...prev, loading: false, errors: true }));
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const confirmDelete = useCallback(
    (value: any) => {
      showConfirm({
        title: 'Устгахдаа итгэлтэй байна уу?',
        content: 'Уг үйлдэл нь буцаагдахгүй',
        okText: 'Тийм',
        cancelText: 'Үгүй',
        onOk() {
          deleteItem(value.no);
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const deleteItem = useCallback(async (no: number) => {
    try {
      await API.delete({ apiVersion: 'api' })(`/settings/barcode/${no}`);
      fetchData();
    } catch (error: any) {
      notifyError(error.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      title: 'No',
      dataIndex: 'no',
    },
    {
      title: 'Угтвар',
      dataIndex: 'prefix',
    },
    {
      title: 'Дагавар',
      dataIndex: 'affix',
    },
    {
      title: 'Үйлдэл',
      render: (record: any) => (
        <div>
          <Button
            size="small"
            style={{ marginRight: 15 }}
            onClick={() => setEdit({ visible: true, data: record })}
          >
            Засах
          </Button>
          <Button size="small" danger onClick={() => confirmDelete(record)}>
            Устгах
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Card
      title="Баркодны тохиргоо"
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setEdit({ visible: true, data: null })}
        >
          Нэмэх
        </Button>
      }
    >
      <Table
        size="small"
        bordered
        loading={state.loading}
        dataSource={state.result || []}
        rowKey="no"
        pagination={false}
        columns={columns}
      />
      <BarcodeSettingsEdit
        visible={edit.visible}
        data={edit.data}
        onClose={() => setEdit({ visible: false, data: null })}
        onSuccess={() => {
          setEdit({ visible: false, data: null });
          fetchData();
        }}
      />
    </Card>
  );
};

type BarcodeSettingsEditProps = {
  visible: boolean;
  data?: any;
  onClose: () => void;
  onSuccess: () => void;
};

const BarcodeSettingsEdit = ({ visible, data, onClose, onSuccess }: BarcodeSettingsEditProps) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (visible) {
      form.setFieldsValue({
        prefix: data?.prefix,
        affix: data?.affix,
      });
    }
  }, [visible, data, form]);

  const close = useCallback(() => {
    form.resetFields();
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (data?.no) {
        await API.put({ apiVersion: 'api' })(`/settings/barcode/${data.no}`, values);
      } else {
        await API.post({ apiVersion: 'api' })(`/settings/barcode`, values);
      }
      form.resetFields();
      onSuccess();
    } catch (error: any) {
      if (error?.errorFields) return;
      notifyError(error.message);
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <Modal
      title={data?.no ? 'Баркодны тохиргоо засах' : 'Баркодны тохиргоо нэмэх'}
      open={visible}
      onCancel={close}
      onOk={handleSubmit}
      confirmLoading={saving}
      okText="Хадгалах"
      cancelText="Болих"
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item label="Угтвар" name="prefix">
          <Input placeholder="Баркодны угтвар" />
        </Form.Item>
        <Form.Item label="Дагавар" name="affix">
          <Input placeholder="Баркодны дагавар" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default BarcodeSettingsList;
